import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { GeneralSelect } from 'sharedComponents';
import { getAssetIcon } from 'helpers/icons';
import { COMPONENT_CONSTANTS } from '../uiConstants';
import './SubTypeSelector.scss';

/**
 * SubTypeSelector - Component for selecting asset subtypes with icon and name display
 */
const SubTypeSelector = memo(({
    asset,
    selectedSubType,
    subTypeOptions = [],
    onSubTypeChange,
    onClear,
    disabled = false
}) => {
    const { t: translate } = useTranslation();
    const [isHovered, setIsHovered] = useState(false);

    const handleChange = (selectedOption) => {
        // Extract the string value from the selected option object
        const selectedValue = selectedOption ? selectedOption.value : null;
        onSubTypeChange(asset, selectedValue);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        if (onClear) {
            onClear(asset);
        } else {
            // Default behavior - clear the selection
            onSubTypeChange(asset, null);
        }
    };

    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    // Format options to include icons and names
    const formattedOptions = subTypeOptions.map((subType) => {
        // Add safety checks for subType data
        if (!subType) {
            return null;
        }

        // Handle both object and string subtypes
        const value = typeof subType === 'string' ? subType : subType.value || subType.id || subType.name || subType;
        const label = typeof subType === 'string' ? subType : subType.label || subType.name || subType.displayName || subType;
        const type = typeof subType === 'string' ? subType : subType.type || subType.name || asset.assetType;

        // Ensure we have valid strings
        if (!value || !label) {
            return null;
        }

        return {
            value: String(value),
            label:
                <div className='subtype-option'>
                    <div className='subtype-icon'>
                        {getAssetIcon(type || asset.assetType, { ...COMPONENT_CONSTANTS.SUBTYPE_ICON_SIZE, color: '#666666' })}
                    </div>
                    <span className='subtype-name'>{String(label)}</span>
                </div>
            ,
            originalLabel: String(label)
        };
    }).filter(Boolean); // Remove any null entries

    const effectiveSelectedSubType = selectedSubType || asset?.selectedSubType || asset?.subType || null;
    const hasSelectedSubType = Boolean(String(effectiveSelectedSubType || '')?.trim());
    const hasInitialSubType = Boolean(String(asset?.subType || '')?.trim());
    const isSelectorDisabled = hasInitialSubType || disabled;

    // Find the selected option object that matches the selected subtype string
    const selectedOption = formattedOptions.find(option => option.value === effectiveSelectedSubType) || null;

    return (
        <div
            className='subtype-selector'
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className='subtype-dropdown-wrapper'>
                <GeneralSelect
                    className='subtype-dropdown'
                    placeholder={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_AssetSelection_SelectPlaceholder')}
                    value={selectedOption}
                    onChange={handleChange}
                    options={formattedOptions}
                    isDisabled={isSelectorDisabled}
                    searchable={false}
                    clearable={false}
                />
                {hasSelectedSubType && isHovered && !isSelectorDisabled &&
                    <button
                        className='clear-icon'
                        onClick={handleClear}
                        type='button'
                        aria-label='Clear selection'
                    >
                        ×
                    </button>
                }
            </div>
        </div>
    );
});

SubTypeSelector.displayName = 'SubTypeSelector';

SubTypeSelector.propTypes = {
    asset: PropTypes.object.isRequired,
    selectedSubType: PropTypes.string,
    subTypeOptions: PropTypes.array,
    onSubTypeChange: PropTypes.func.isRequired,
    onClear: PropTypes.func,
    disabled: PropTypes.bool
};

export default SubTypeSelector;
